import { Column, FilterFnOption } from "@tanstack/react-table";

import { DataTableContextProvider } from "./datatable-context/data-table-context-provider";
import { DatatableBody } from "./datatable-body";
import { DatatableCellCacheProvider } from "./datatable-cell-cache";
import { DatatableNoContext, type DatatableNoContextProps } from "./datatable-no-context";
import { DatatableRowCacheProvider } from "./datatable-row-cache";
import { type DatatableColumn, DatatableRowData, type DatatableState, TSectionData } from "./datatable-types";
import { useUnsafeDatatableContext } from "./use-datatable-context";

type DatatableProps<TData extends DatatableRowData> = {
  workspaceId: string;
  data: TData[];
  columns: DatatableColumn<TData>[];
  initialState?: DatatableState;
  debugTable?: boolean;
  synchToApi?: boolean;
  initWithUrlParams?: boolean;
  pageSize?: number;

  globalFilterFn?: FilterFnOption<TSectionData<TData>>;
  getColumnCanGlobalFilter?: (column: Column<TSectionData<TData>, unknown>) => boolean;
} & DatatableNoContextProps<TData>;

export function Datatable<TData extends DatatableRowData>({
  config,
  children,
  getRowPath,
  onRowClick,
  onRowHover,
  isRowSelected,
  ...props
}: DatatableProps<TData>) {
  const maybeDataTableContext = useUnsafeDatatableContext();
  if (!maybeDataTableContext) {
    return (
      <DatatableRowCacheProvider>
        <DatatableCellCacheProvider viewKey={props.viewKey}>
          <DataTableContextProvider<TData> {...props} lockPublishedConfiguration={config?.lockPublishedConfiguration}>
            <DatatableBody<TData>
              config={config}
              getRowPath={getRowPath}
              onRowClick={onRowClick}
              onRowHover={onRowHover}
              isRowSelected={isRowSelected}
            />
            {children}
          </DataTableContextProvider>
        </DatatableCellCacheProvider>
      </DatatableRowCacheProvider>
    );
  }

  return (
    <DatatableNoContext
      viewKey={props.viewKey}
      config={config}
      getRowPath={getRowPath}
      onRowClick={onRowClick}
      onRowHover={onRowHover}
      isRowSelected={isRowSelected}
    >
      {children}
    </DatatableNoContext>
  );
}
